import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import PrefetchLink from "@/components/PrefetchLink";
import { Helmet } from "react-helmet-async";
import { Shield, DollarSign, Eye, Cpu, Smartphone, Lock, ScanLine, ChevronRight, Star, ArrowRight, MapPin, Instagram, Linkedin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import BrazilMap from "@/components/BrazilMap";
import { useAtuacao } from "@/hooks/useAtuacao";
import ClientesSection from "@/components/ClientesSection";
import ParceirosSection from "@/components/ParceirosSection";
import { breadcrumbJsonLd, serviceJsonLd } from "@/lib/seo";

const useCountUp = (end: number, duration = 2000) => {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const start = performance.now();
          const step = (now: number) => {
            const progress = Math.min((now - start) / duration, 1);
            setCount(Math.floor(progress * end));
            if (progress < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, duration]);

  return { count, ref };
};

const benefits = [
{ icon: DollarSign, title: "Redução de Custos", desc: "Economize até 50% nos custos com portaria tradicional sem perder qualidade." },
{ icon: Shield, title: "Segurança 24h", desc: "Monitoramento ininterrupto com equipe treinada e tecnologia de ponta." },
{ icon: Cpu, title: "Monitoramento com IA", desc: "Inteligência artificial para leitura de placas e detecção de eventos." },
{ icon: Smartphone, title: "App para Moradores", desc: "Libere visitantes, acompanhe acessos e receba notificações em tempo real." }];


const heroImages: Record<string, string> = {
  goiania: "/hero-goiania.webp",
  // cuiaba: "/hero-cuiaba.webp",
  // saopaulo: "/hero-saopaulo.webp",
};

const solutions = [
{ icon: Eye, title: "Portaria Remota 24h", desc: "Controle total de acesso com operadores remotos dedicados ao seu condomínio.", path: "/solucoes" },
{ icon: Cpu, title: "Monitoramento Inteligente", desc: "IA que identifica placas, pessoas e eventos automaticamente.", path: "/solucoes" },
{ icon: ScanLine, title: "Controle de Acesso", desc: "QR Code, biometria e app do morador para entrada rápida e segura.", path: "/solucoes" }];


const testimonials = [
{ name: "Carlos M.", role: "Síndico – Cond. Parque das Flores", text: "Reduzimos nossos custos em 45% e a segurança melhorou significativamente.", rating: 5 },
{ name: "Ana P.", role: "Administradora – Premium Imóveis", text: "A tecnologia de leitura de placas é impressionante. Os moradores adoraram o app.", rating: 5 },
{ name: "Roberto S.", role: "Síndico – Residencial Alto da Glória", text: "Atendimento excelente e implantação rápida. Recomendo para qualquer condomínio.", rating: 5 }];


const Index = () => {
  const counter1 = useCountUp(13);
  const counter2 = useCountUp(600);
  const counter3 = useCountUp(1000000);
  const { data: estados = [] } = useAtuacao();

  return (
    <main>
      <Helmet>
        <title>Portaria Remota em Goiânia | Acesso Sistemas de Segurança</title>
        <meta name="description" content="Portaria remota 24h, monitoramento com IA e controle de acesso para condomínios em Goiânia e região metropolitana. Economia de até 50%." />
        <link rel="canonical" href="https://acesso.srv.br/" />
        <meta property="og:title" content="Portaria Remota em Goiânia | Acesso Sistemas de Segurança" />
        <meta property="og:description" content="Portaria remota 24h, monitoramento com IA e controle de acesso para condomínios em Goiânia e região metropolitana." />
        <meta property="og:url" content="https://acesso.srv.br/" />
        <meta property="og:image" content="https://acesso.srv.br/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Portaria Remota em Goiânia | Acesso Sistemas de Segurança" />
        <meta name="twitter:description" content="Portaria remota 24h, monitoramento com IA e controle de acesso para condomínios em Goiânia e região metropolitana." />
        <script type="application/ld+json">{JSON.stringify(serviceJsonLd({
          name: "Portaria Remota em Goiânia",
          description: "Portaria remota 24h com operadores treinados, monitoramento por IA e controle de acesso para condomínios em Goiânia e região metropolitana.",
          serviceType: "Portaria Remota",
          url: "/",
        }))}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd([
          { name: "Início", path: "/" },
        ]))}</script>
      </Helmet>
      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-center text-white overflow-hidden">
        <picture>
          <source
            type="image/avif"
            srcSet="/hero-goiania-640.avif 640w, /hero-goiania-1280.avif 1280w, /hero-goiania.avif 1920w"
            sizes="100vw"
          />
          <source
            type="image/webp"
            srcSet="/hero-goiania-640.webp 640w, /hero-goiania-1280.webp 1280w, /hero-goiania.webp 1920w"
            sizes="100vw"
          />
          <img
            src="/hero-goiania.webp"
            alt="Vista aérea noturna de Goiânia representando atuação da Acesso Sistemas de Segurança"
            className="absolute inset-0 w-full h-full object-cover object-center"
            fetchPriority="high"
            decoding="async"
            width={1920}
            height={1080}
          />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-r from-[rgba(15,30,50,0.85)] to-[rgba(15,30,50,0.70)]" />
        <div className="container mx-auto px-4 py-20 relative z-10">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-accent/20 text-accent rounded-full text-sm font-medium mb-6">
              <Shield className="h-4 w-4" /> Segurança Inteligente
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif", textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>
              Portaria Remota Inteligente em Goiânia
            </h1>
            <p className="text-lg md:text-xl text-primary-foreground/80 mb-8 leading-relaxed">
              Reduza custos, aumente a segurança e ofereça tecnologia de ponta para o seu condomínio com monitoramento 24 horas e controle por aplicativo.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 text-base px-8">
                <PrefetchLink to="/contato">Solicitar Orçamento <ArrowRight className="ml-2 h-5 w-5" /></PrefetchLink>
              </Button>
              <Button asChild size="lg" className="bg-transparent border border-white/40 text-white hover:bg-white/10 text-base px-8">
                <a href="https://wa.me/5562999999999" target="_blank" rel="noopener noreferrer">
                  Falar no WhatsApp
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Por que condomínios de Goiânia escolhem a Acesso
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Tecnologia, economia e segurança reunidas em uma solução completa para o seu condomínio.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b) =>
            <Card key={b.title} className="border-none shadow-md hover:shadow-lg transition-shadow group">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 rounded-xl bg-accent/10 text-accent flex items-center justify-center mx-auto mb-4 group-hover:bg-accent group-hover:text-accent-foreground transition-colors">
                    <b.icon className="h-7 w-7" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">{b.title}</h3>
                  <p className="text-sm text-muted-foreground">{b.desc}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section className="py-20 bg-muted">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Soluções de Segurança para Condomínios em Goiânia
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Soluções integradas de segurança eletrônica para condomínios de todos os portes.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {solutions.map((s) =>
            <PrefetchLink key={s.title} to={s.path}>
                <Card className="h-full border-none shadow-md hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer group">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-5 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                      <s.icon className="h-7 w-7" />
                    </div>
                    <h3 className="font-bold text-xl mb-3">{s.title}</h3>
                    <p className="text-muted-foreground text-sm mb-4">{s.desc}</p>
                    <span className="text-accent font-medium text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                      Saiba mais <ChevronRight className="h-4 w-4" />
                    </span>
                  </CardContent>
                </Card>
              </PrefetchLink>
            )}
          </div>
        </div>
      </section>

      {/* Counters */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div ref={counter1.ref}>
              <div className="text-5xl font-bold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>+{counter1.count}</div>
              <p className="text-primary-foreground/70">Anos de Experiência</p>
            </div>
            <div ref={counter2.ref}>
              <div className="text-5xl font-bold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>+{counter2.count}</div>
              <p className="text-primary-foreground/70">Condomínios Atendidos</p>
            </div>
            <div ref={counter3.ref}>
              <div className="text-5xl font-bold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>+{counter3.count >= 1000000 ? "1M" : Math.floor(counter3.count / 1000) + "K"}</div>
              <p className="text-primary-foreground/70">Eventos Monitorados</p>
            </div>
          </div>
        </div>
      </section>

      {/* Nossos Clientes */}
      <ClientesSection />

      {/* Nossos Parceiros */}
      <ParceirosSection />

      {/* Atuação Nacional */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Presença Nacional
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Operação consolidada em 5 estados, com sede em Goiânia e expansão estratégica pelo Brasil.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <BrazilMap estados={estados} compact />
            <div>
              <ul className="space-y-3 mb-6">
                {estados.map((e) =>
                <li key={e.id} className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-accent shrink-0" />
                    <div>
                      <span className="font-semibold">{e.nome_estado}</span>
                      <span className="text-sm text-muted-foreground ml-2">
                        {e.cidades.map((c) => c.nome_cidade).join(", ")}
                      </span>
                    </div>
                  </li>
                )}
              </ul>
              <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90">
                <PrefetchLink to="/atuacao">Ver Atuação Completa <ArrowRight className="ml-2 h-4 w-4" /></PrefetchLink>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              O que dizem nossos clientes
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t) =>
            <Card key={t.name} className="border-none shadow-md">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {Array.from({ length: t.rating }).map((_, i) =>
                  <Star key={i} className="h-4 w-4 fill-accent text-accent" />
                  )}
                  </div>
                  <p className="text-sm text-muted-foreground mb-4 italic">"{t.text}"</p>
                  <div>
                    <p className="font-semibold text-sm">{t.name}</p>
                    <p className="text-xs text-muted-foreground">{t.role}</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Social */}
      <section className="py-16 bg-muted">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Acompanhe nossas novidades
          </h2>
          <p className="text-muted-foreground max-w-md mx-auto mb-8">
            Fique por dentro das atualizações, implantações e inovações em segurança.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild variant="outline" size="lg" className="text-base">
              <a href="https://www.instagram.com/acesso.srv/" target="_blank" rel="noopener noreferrer">
                <Instagram className="mr-2 h-5 w-5" /> Instagram
              </a>
            </Button>
            <Button asChild variant="outline" size="lg" className="text-base">
              <a href="https://br.linkedin.com/company/acesso-sistemas-de-seguran%C3%A7a" target="_blank" rel="noopener noreferrer">
                <Linkedin className="mr-2 h-5 w-5" /> LinkedIn
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-accent text-accent-foreground">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Pronto para modernizar seu condomínio?
          </h2>
          <p className="text-accent-foreground max-w-lg mx-auto mb-8">
            Solicite um orçamento sem compromisso e descubra como a portaria remota pode transformar a segurança do seu condomínio.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 text-base px-8">
              <PrefetchLink to="/contato">Solicitar Orçamento</PrefetchLink>
            </Button>
            <Button asChild size="lg" className="bg-transparent border border-white/40 text-white hover:bg-white/10 text-base px-8">
              <a href="https://wa.me/5562999999999" target="_blank" rel="noopener noreferrer">
                Falar no WhatsApp
              </a>
            </Button>
          </div>
        </div>
      </section>
    </main>);

};

export default Index;